import Head from 'next/head';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetServerSideProps } from 'next';
import { ParsedUrlQuery } from 'querystring';
import { CvDetailHub } from '@components/templates/cv/CvDetailHub';
import { useTranslation } from 'next-i18next';
import { gsspCheckCookieAuth, gsspGetLocale } from '@utils/gssp';
import { LayoutDefault } from '@components/layout/Layouts/LayoutDefault';
import { ReactNode } from 'react';


// Interfaces
// ************************************
interface CvDetailPageProps {
  slug: string,
  locale: Locale,
  isAuthenticated: boolean,
  closeModal?: () => void,
}

interface IParams extends ParsedUrlQuery {
  slug: string,
}


/**
 * @description CV Detail page. Used in the Talent Search context.
 * - **slug** - string - The CV slug.
 * - **isAuthenticated** - boolean - If the user is authenticated.
 * - **closeModal** - function - Function to close the modal (if viewing in a modal).
 */
const CvDetail = (props: CvDetailPageProps) => {
  const {
    slug,
    locale,
    isAuthenticated,
    closeModal,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Render the CV page
  // ************************************
  return (
    <>
      {/* Page title and meta */}
      <Head>
        <title>{t('cv.detail.meta.title')}</title>
        <meta name="description" content={t('cv.detail.meta.description')} />
      </Head>
      <CvDetailHub
        isSinglePage
        slug={slug}
        isAuthenticated={isAuthenticated}
        closeModal={closeModal}
      />
    </>
  );
};


/**
 * @description Get the server-side props
 * @param context - The context object
 */
export const getServerSideProps: GetServerSideProps = async (context) => {
  // Destructure the context
  const {
    params,
  } = context;

  const locale = gsspGetLocale(params);

  // Get the slug from the params
  const { slug } = params as IParams;

  // Check if the user is authenticated
  const isAuthenticated = await gsspCheckCookieAuth({ context });

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      slug,
      locale,
      isAuthenticated,
    },
  };
};


/**
 * @description Export the page component.
 */
CvDetail.getLayout = (page: ReactNode) => (
  <LayoutDefault>
    {page}
  </LayoutDefault>
);

export default CvDetail;
